import {Component, computed, inject, signal, WritableSignal, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle} from "@angular/material/dialog"
import {MatInputModule} from "@angular/material/input"
import {MatFormFieldModule} from "@angular/material/form-field"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {SaveMaterialRevisionDialogMaterialGQL} from "@material-editor/components/misc/save-material-revision-dialog/save-material-revision-dialog.generated"
import {MaterialMapsExporterCore} from "@cm/material-maps-exporter"
import type {MaterialGraph} from "@cm/material-nodes"
import {DataResolverService} from "@material-editor/services/data-resolver.service"

/** One thing to tell the author about the export, named by the channel it belongs to where there is one. */
type MaterialExportWarning = {slot?: string; message: string; severity: MaterialMapsExporterCore.Analyze.MaterialGraphIssueSeverity}

const warningForIssue = (issue: MaterialMapsExporterCore.Analyze.MaterialGraphIssue): MaterialExportWarning => ({
    slot: issue.bsdfInputLabel,
    message: issue.message,
    severity: issue.severity,
})

const joinOr = (labels: string[]): string => (labels.length <= 1 ? (labels[0] ?? "") : `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`)

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-save-material-revision-dialog",
    templateUrl: "./save-material-revision-dialog.component.html",
    styleUrls: ["./save-material-revision-dialog.component.scss"],
    imports: [MatDialogContent, MatDialogTitle, MatInputModule, FormsModule, MatDialogActions, MatButtonModule, MatFormFieldModule],
})
export class SaveMaterialRevisionDialogComponent {
    readonly materialGql = inject(SaveMaterialRevisionDialogMaterialGQL)

    readonly $result: WritableSignal<SaveMaterialRevisionDialogResult>
    readonly $materialInfo = signal<{
        name: string
        legacyId: number
        customer?: string
        range?: string
    } | null>(null)

    readonly permission = inject(PermissionsService)
    $can = this.permission.$to
    readonly dialogRef = inject(MatDialogRef<SaveMaterialRevisionDialogComponent>)
    readonly data = inject<{materialId: string; materialGraph?: MaterialGraph}>(MAT_DIALOG_DATA)
    private readonly dataResolver = inject(DataResolverService)

    /** Warn, never block: a graph the exporter cannot read is still a valid material to render. */
    readonly $exportWarnings = signal<MaterialExportWarning[]>([])
    /** Drives the heading: a material whose every warning is advisory is one the exporter reads fine. */
    readonly $hasBlockingWarning = computed(() => this.$exportWarnings().some((warning) => warning.severity === "blocking"))
    /** Worst first, each group left in channel order, so what refuses the export is read before what does not. */
    readonly $orderedWarnings = computed(() =>
        [...this.$exportWarnings()].sort((a, b) => Number(b.severity === "blocking") - Number(a.severity === "blocking")),
    )
    readonly exportableChainHint = (() => {
        const {heads, transforms, adapters} = MaterialMapsExporterCore.Types.supportedChainNodeLabels()
        return (
            `The exporter follows one chain per channel: each must start at ${joinOr(heads)}, optionally going through ${joinOr(transforms)}, ` +
            `and may pass through ${joinOr(adapters)}.`
        )
    })()

    constructor() {
        if (!this.data) {
            throw new Error("No material id!")
        }
        this.$result = signal<SaveMaterialRevisionDialogResult>({materialId: this.data.materialId, comment: ""})
        void this.materialChanged(this.data.materialId)
        if (this.data.materialGraph) this.warnAboutExportability(this.data.materialGraph)
    }

    /**
     * What the maps exporter would refuse about this graph, computed here rather than asked of the server:
     * the shape half needs nothing but the graph, and the data half resolves through the same resolver the
     * editor already uses for render naming. Unconnected slots are not warnings — an export fills them.
     */
    private warnAboutExportability(graph: MaterialGraph) {
        const analysis = MaterialMapsExporterCore.Analyze.analyzeMaterialGraph(graph)
        if (analysis.kind === "noBsdf") {
            this.$exportWarnings.set([{message: MaterialMapsExporterCore.Analyze.noBsdfMessage(analysis), severity: "blocking"}])
            return
        }
        this.$exportWarnings.set(
            [...MaterialMapsExporterCore.Analyze.unreadableSlotIssues(analysis), ...MaterialMapsExporterCore.Analyze.advisoryIssues(analysis)].map(
                warningForIssue,
            ),
        )
        void this.appendDataWarnings(analysis)
    }

    /**
     * The half that needs the referenced textures resolved. It must never delay the dialog, so it is not
     * awaited, and a resolver that fails leaves the shape warnings standing rather than reporting a
     * problem with the material: an unresolvable reference is this check failing, not the graph being wrong.
     */
    private async appendDataWarnings(analysis: Extract<MaterialMapsExporterCore.Analyze.MaterialGraphAnalysis, {kind: "bsdf"}>) {
        try {
            const slots = await MaterialMapsExporterCore.Gather.resolveSlotHeads(analysis, {
                resolveTextureRevision: this.dataResolver.resolve.textureRevisionLegacy,
                resolveDataObject: this.dataResolver.resolve.dataObject,
                resolveTextureSetRevision: this.dataResolver.resolve.textureSetRevision,
            })
            // Shape issues pass through the resolution, and are already shown.
            const dataWarnings = MaterialMapsExporterCore.Gather.issuesForResolvedSlots(slots)
                .filter((issue) => issue.category === "data")
                .map(warningForIssue)
            if (dataWarnings.length > 0) this.$exportWarnings.update((warnings) => [...warnings, ...dataWarnings])
        } catch {
            // Deliberately silent: see above.
        }
    }

    async materialChanged(materialId?: string, materialLegacyId?: number) {
        if (!materialId && !materialLegacyId) {
            this.$materialInfo.set(null)
            return
        }
        const material = await fetchThrowingErrors(this.materialGql)({id: materialId, legacyId: materialLegacyId})
            .then((response) => response.material)
            .catch(() => null)

        if (!material) {
            if (materialLegacyId) {
                this.$materialInfo.set({name: "", legacyId: materialLegacyId})
            } else {
                this.$materialInfo.set(null)
            }
            return
        }
        const materialRangeTag = material.tagAssignments[0]?.tag?.name
        this.$materialInfo.set({
            name: material.name ?? "",
            legacyId: material.legacyId,
            range: materialRangeTag ?? "",
            customer: material.organization?.name ?? undefined,
        })
        this.$result.update((current) => ({...current, materialId: material.id}))
    }

    cancelAction(): void {
        this.dialogRef.close(false)
    }

    confirmAction(): void {
        this.dialogRef.close(this.$result())
    }
}

export type SaveMaterialRevisionDialogResult = {
    materialId: string
    comment: string
}
